import React, { useState, useEffect } from "react";
import TopViewNav from "../CommonComponents/TopViewNav";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ClientAction } from "../../actions/client";
import UseModal from "../Modal/Modal";
import { companyWiseEmp } from "../../actions/Companywiseclient";
import { exportUserListExcel } from "./userExportExcelHelper";
import Excel from "../../img/excel.png";
import { userExportBlob } from "./userExportBlob";
import { useLocalStorage } from "../../Hooks/Index";

const Users = () => {
  const localData = JSON.parse(localStorage.getItem("data")) || {};
  const [hrmsUsers, setHrmsUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [isEnableUndertakingFormActive, setIsEnableUndertakingFormActive] =
    useState(false);
  const [isExcelDataLoading, setIsExcelDataLoading] = useState(false);
  const [selectedSite] = useLocalStorage("selectedSite", null);

  // New states for travel modes
  const [travelModes, setTravelModes] = useState([]);
  const [loadingModes, setLoadingModes] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null); // track which user is being updated

  const topViewNavData = {
    title: "Users",
    backPathRoute: "/dashboard",
  };

  // Fetch all travel modes (GET)
  const fetchTravelModes = async () => {
    setLoadingModes(true);
    try {
      const response = await ClientAction.fetchRouteMode(); // as per note
      if (response && response.responseCode === 200) {
        setTravelModes(response.data);
      }
    } catch (error) {
      console.error("Error fetching travel modes:", error);
    } finally {
      setLoadingModes(false);
    }
  };

  // Fetch users list
  const fetchHrmsUsers = async () => {
    const response = await ClientAction.getHrmsUsers({
      hrmsToken: JSON.parse(localStorage.getItem("data"))?.hrmsToken,
      hrmsUserId: JSON.parse(localStorage.getItem("data"))?.hrmsUserId,
      userId: JSON.parse(localStorage.getItem("data"))?.id,
      siteId: selectedSite ? selectedSite?.id : null,
    });
    if (response !== null && response.responseCode === 200) {
      setHrmsUsers(response.data);
    }
  };

  const handleViewClick = (undertakingFormUrl) => {
    window.open(undertakingFormUrl, "_blank");
  };

  const fetchClientBaseEmpForUsers = () => {
    const companyId = JSON.parse(localStorage.getItem("data"))?.hrmsCompanyId;
    const data = {
      companyId: companyId ? companyId : null,
    };
    companyWiseEmp.fetchCompnyWiseUpdate(data).then((response) => {
      if (response !== null && response.responseCode === 200) {
        const enableUndertakingFormToSet =
          response.data &&
          response.data.filter(
            (val) => val.hrmsConfigurationName === "ENABLE_UNDERTAKING_FORM",
          );
        setIsEnableUndertakingFormActive(
          enableUndertakingFormToSet && enableUndertakingFormToSet.length > 0
            ? enableUndertakingFormToSet[0].isEnable === true
              ? true
              : false
            : false,
        );
      }
    });
  };

  const fetchExportUserExcel = async () => {
    setIsExcelDataLoading(true);
    let hrmsID = JSON.parse(localStorage.getItem("data"))?.hrmsUserId;
    let userId = JSON.parse(localStorage.getItem("data"))?.id;

    const userPayload = {
      hrmsUserId: hrmsID,
      userId: userId,
      isDepartmentVisible: JSON.parse(localStorage.getItem("data"))
        ?.isUndertakingFormEnabled,
      hrmsToken: JSON.parse(localStorage.getItem("data"))?.hrmsToken,
    };
    const res = await ClientAction.exportUserExcel(userPayload);
    if (res !== null) {
      userExportBlob(res);
    } else {
      console.error("No data to export");
    }
    setIsExcelDataLoading(false);
  };

  const userExport = () => {
    fetchExportUserExcel();
  };

  // Handle travel mode change for a user
  const handleModeChange = async (user, newModeCode) => {
    // Build payload as per API spec
    const payload = [
      {
        userId: user.userId,
        companyId: localData?.hrmsCompanyId, // Ensure these fields exist in user object
        hrmsId: user.hrmsUserId,
        googleDirectionsMode: newModeCode,
      },
    ];

    setUpdatingUserId(user.userId);
    try {
      const response = await ClientAction.updateUserRouteMode(payload); // update API
      if (response && response.responseCode === 200) {
        // Refresh user list to get updated data
        await fetchHrmsUsers();
      } else {
        alert(response?.responseDescription || "Failed to update travel mode");
      }
    } catch (error) {
      alert("Internal server error while updating travel mode");
      console.error("Error updating travel mode:", error);
    } finally {
      setUpdatingUserId(null);
    }
  };

  useEffect(() => {
    fetchHrmsUsers();
    fetchClientBaseEmpForUsers();
    fetchTravelModes(); // Fetch travel modes on mount
  }, [selectedSite]);

  const filteredUsers = hrmsUsers.filter((user) => {
    const keyword = search.toLowerCase();
    return (
      user.name?.toLowerCase().includes(keyword) ||
      user.empCode?.toLowerCase().includes(keyword)
    );
  });

  // Compute default mode code (the one with isSelected: true)
  const defaultModeCode =
    travelModes.find((mode) => mode.isSelected)?.code || "";

  const adminIds = localData && localData?.adminIds?.includes(localData?.hrmsUserId);

  return (
    <main className="contents__root">
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TopViewNav topViewNavData={topViewNavData} />
          <Box sx={{ my: 2 }}>
            <Grid container spacing={1}>
              <Grid item xs={6} md={4}>
                <div className="field_box">
                  <TextField
                    size="small"
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </Grid>

              <Grid item xs={6} md={8}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <Tooltip
                    title={
                      isExcelDataLoading
                        ? "Data is Loading Please wait."
                        : "Export Excel"
                    }
                    sx={{ width: "35px", height: "15px" }}
                    onClick={userExport}
                  >
                    <img
                      src={Excel}
                      style={{
                        width: "30px",
                        marginTop: "2px",
                        cursor: "pointer",
                      }}
                      alt="Export Excel"
                    />
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small"
            sx={{
                    minWidth: 1200,

                    "& tbody td": {
                      border: "0.5px solid #d0d7de",
                    },

                    // First column
                    "& th:first-of-type, & td:first-of-type": {
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                      // backgroundColor: "#fff",
                    },

                    "td:first-of-type": {
                      backgroundColor: "#fff",
                    },

                    // Header cells
                    "& thead th": {
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                      backgroundColor: "rgb(32 80 130)",
                      border: "none",
                    },

                    // Top-left cell needs highest z-index
                    "& thead th:first-of-type": {
                      left: 0,
                      zIndex: 4,
                    },
                  }}>
              <TableHead>
                <TableRow
                  style={{
                    backgroundColor: "rgb(32 80 130)",
                    color: "#fff",
                  }}
                  align="center"
                >
                  <TableCell style={{ color: "#fff" }} align="center">
                    S.No.
                  </TableCell>
                  <TableCell style={{ color: "#fff" }} align="center">
                    Username
                  </TableCell>
                  {/* New Action column */}
                  {localData?.enableCompanyWiseUserGoogleRouteMode && (
                    <TableCell style={{ color: "#fff" }} align="center">
                      Action
                    </TableCell>
                  )}
                  {Boolean(localData?.isDepartmentVisible) ? (
                    <TableCell style={{ color: "#fff" }} align="center">
                      {JSON.parse(localStorage.getItem("Templatedata")).find(
                        (label) => label.actualName === "Department",
                      )?.displayName || "Department"}
                    </TableCell>
                  ) : (
                    ""
                  )}

                  {isEnableUndertakingFormActive ? (
                    <TableCell style={{ color: "#fff" }} align="center">
                      Is Undertaking Submitted
                    </TableCell>
                  ) : null}
                  {isEnableUndertakingFormActive ? (
                    <TableCell style={{ color: "#fff" }} align="center">
                      View Form Detail
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers?.map((user, index) => {
                  // Determine current mode for this user
                  const currentModeCode =
                    user.googleDirectionsMode || defaultModeCode;
                  const currentModeObject =
                    travelModes?.find(
                      (mode) =>
                        mode.displayName?.toLowerCase() ===
                        currentModeCode?.toLowerCase(),
                    ) || null;                  
                  
                  return (
                    <TableRow hover key={user.id}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{`${user?.name}(${user?.empCode})`}</TableCell>

                      {/* Action cell with Autocomplete */}
                      {localData?.enableCompanyWiseUserGoogleRouteMode && (
                        <TableCell align="center">
                          {loadingModes ? (
                            <CircularProgress size={24} />
                          ) : (
                            <Autocomplete
                              size="small"
                              options={travelModes.map((mode) => ({
                                displayName: mode.displayName,
                                code: mode.code,
                              }))}
                              getOptionLabel={(option) => option.displayName}
                              value={currentModeObject}
                              onChange={(event, newValue) => {
                                if (newValue) {
                                  handleModeChange(user, newValue.code);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  size="small"
                                  placeholder="Select mode"
                                />
                              )}
                              isOptionEqualToValue={(option, value) =>
                                option.code === value.code
                              }
                              loading={updatingUserId === user.id}
                              disabled={!adminIds || updatingUserId === user.id}
                              style={{ minWidth: 150 }}
                            />
                          )}
                        </TableCell>
                      )}

                      {Boolean(localData?.isDepartmentVisible) ? (
                        <TableCell align="center">
                          {user.departmentName}
                        </TableCell>
                      ) : (
                        ""
                      )}

                      {isEnableUndertakingFormActive ? (
                        <TableCell align="center">
                          {user.isSignatureCompleted ? "Yes" : "No"}
                        </TableCell>
                      ) : null}
                      {isEnableUndertakingFormActive ? (
                        <TableCell align="center">
                          {user.departmentUndertakingFormMappingDtos &&
                            user.departmentUndertakingFormMappingDtos.map(
                              (obj) => (
                                <Link
                                  key={obj.id}
                                  href={obj.docUrl}
                                  target="_blank"
                                  sx={{ mx: 1 }}
                                >
                                  {obj.formName}
                                </Link>
                              ),
                            )}
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </main>
  );
};

function CustomPDFViewerModal({ title, item, icon }) {
  return (
    <UseModal button={icon} title={title} width={"65%"}>
      <iframe
        src={item}
        title="PDF Viewer"
        width={"100%"}
        height={"410px"}
      ></iframe>
    </UseModal>
  );
}

export default Users;
